import {
  generateBulkSinglePdf,
  printPdfBlob,
} from '../../utils/scoringPdfGenerator';
import {
  getGradeOnlySummary,
  isGradeOnlyResult,
} from './scoringGradeMode';
import { getScoringStudentName } from './scoringResultMeta';
import { getScoringResultTotalForExport } from './scoringExportData';

export const escapeScoringPdfHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const buildScoreLabel = (result, rubric) => (
  isGradeOnlyResult(result, rubric)
    ? getGradeOnlySummary(result)
    : `${getScoringResultTotalForExport(result)}점`
);

export const buildScoringResultPdfHtml = (
  result,
  { excludeTeacherComment = true, rubric = null } = {},
) => {
  const studentName = escapeScoringPdfHtml(getScoringStudentName(result, '학생'));
  let html = '<div>';

  html += '<div data-pdf-section>';
  html += `<table style="width:100%;margin-bottom:16px;"><tr>
    <td style="font-size:22px;font-weight:700;color:#111827;">${studentName}</td>
    <td style="font-size:28px;font-weight:800;color:#3182f6;text-align:right;white-space:nowrap;">${escapeScoringPdfHtml(buildScoreLabel(result, rubric))}</td>
  </tr></table>`;
  html += '</div>';

  if (result.question_results?.length > 0) {
    html += '<div data-pdf-section style="margin-bottom:16px;"><div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:10px;">문항별 평가</div>';
    result.question_results.forEach((question) => {
      const questionScore = isGradeOnlyResult(result, rubric)
        ? (question.matched_level || getGradeOnlySummary(question))
        : `${question.score || 0}/${question.max_score || 0}점`;
      html += `<div style="background:#f9fafb;border-radius:8px;padding:10px 14px;margin-bottom:8px;">
        <table style="width:100%;"><tr>
          <td style="font-weight:600;font-size:13px;color:#374151;">${escapeScoringPdfHtml(question.question_title || `${question.question_id}번 문항`)}</td>
          <td style="font-weight:700;font-size:13px;color:#3182f6;text-align:right;white-space:nowrap;">${escapeScoringPdfHtml(questionScore)}</td>
        </tr></table>`;
      if (question.answer_excerpt) {
        html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;">답안: ${escapeScoringPdfHtml(question.answer_excerpt)}</div>`;
      }
      (question.item_scores || []).forEach((item) => {
        const itemScore = isGradeOnlyResult(result, rubric)
          ? (item.matched_level || '등급')
          : `${item.score || 0}/${item.max_score || 0}점`;
        html += `<div style="background:#fff;border-radius:8px;padding:8px 10px;margin-top:8px;">
          <table style="width:100%;"><tr>
            <td style="font-weight:600;font-size:12px;color:#374151;">${escapeScoringPdfHtml(item.item_name)}</td>
            <td style="font-weight:700;font-size:12px;color:#3182f6;text-align:right;white-space:nowrap;">${escapeScoringPdfHtml(itemScore)}</td>
          </tr></table>`;
        if (item.reason) {
          html += `<div style="font-size:12px;color:#6b7280;line-height:1.7;margin-top:4px;">${escapeScoringPdfHtml(item.reason)}</div>`;
        }
        html += '</div>';
      });
      html += '</div>';
    });
    html += '</div>';
  }

  if (result.item_scores?.length > 0) {
    html += '<div data-pdf-section style="margin-bottom:16px;"><div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:10px;">항목별 평가</div>';
    result.item_scores.forEach((item) => {
      const itemScore = isGradeOnlyResult(result, rubric)
        ? (item.matched_level || '등급')
        : `${item.score || 0}/${item.max_score || 0}점`;
      html += `<div style="background:#f9fafb;border-radius:8px;padding:10px 14px;margin-bottom:8px;">
        <table style="width:100%;"><tr>
          <td style="font-weight:600;font-size:13px;color:#374151;">${escapeScoringPdfHtml(item.item_name)}</td>
          <td style="font-weight:700;font-size:13px;color:#3182f6;text-align:right;white-space:nowrap;">${escapeScoringPdfHtml(itemScore)}</td>
        </tr></table>`;
      if (item.matched_level) {
        html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;"><span style="background:#e5e7eb;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;margin-right:6px;display:inline-block;">평가 기준</span>${escapeScoringPdfHtml(item.matched_level)}</div>`;
      }
      if (item.reason) {
        html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;">${escapeScoringPdfHtml(item.reason)}</div>`;
      }
      html += '</div>';
    });
    html += '</div>';
  }

  if (result.strengths?.length > 0 || result.improvements?.length > 0) {
    html += '<div data-pdf-section><table style="width:100%;margin-bottom:16px;border-spacing:8px;"><tr>';
    if (result.strengths?.length > 0) {
      html += '<td style="vertical-align:top;background:#f0f7ff;border-radius:10px;padding:14px;width:50%;"><div style="font-size:13px;font-weight:700;color:#3182f6;margin-bottom:8px;">잘한 점</div><ul style="margin:0;padding-left:18px;">';
      result.strengths.forEach((text) => {
        html += `<li style="font-size:13px;color:#374151;margin-bottom:4px;line-height:1.6;">${escapeScoringPdfHtml(text)}</li>`;
      });
      html += '</ul></td>';
    }
    if (result.improvements?.length > 0) {
      html += '<td style="vertical-align:top;background:#fffbeb;border-radius:10px;padding:14px;width:50%;"><div style="font-size:13px;font-weight:700;color:#f59e0b;margin-bottom:8px;">보완할 점</div><ul style="margin:0;padding-left:18px;">';
      result.improvements.forEach((text) => {
        html += `<li style="font-size:13px;color:#374151;margin-bottom:4px;line-height:1.6;">${escapeScoringPdfHtml(text)}</li>`;
      });
      html += '</ul></td>';
    }
    html += '</tr></table></div>';
  }

  if (result.detailed_feedback) {
    html += `<div data-pdf-section style="margin-bottom:12px;">
      <div style="font-weight:700;font-size:14px;margin-bottom:6px;">종합 의견</div>
      <div style="background:#f9fafb;border-radius:8px;padding:12px;font-size:13px;line-height:1.8;">${escapeScoringPdfHtml(result.detailed_feedback)}</div>
    </div>`;
  }

  if (!excludeTeacherComment && result.teacher_comment) {
    html += `<div data-pdf-section style="margin-bottom:12px;">
      <div style="font-weight:700;font-size:14px;color:#15803d;margin-bottom:6px;">교과/세특 생기부</div>
      <div style="background:#f0fdf4;border-radius:8px;padding:12px;font-size:13px;color:#166534;line-height:1.8;">${escapeScoringPdfHtml(result.teacher_comment)}</div>
    </div>`;
  }

  html += '</div>';
  return html;
};

export const downloadScoringResultsPdf = async ({
  results,
  subject = '전체',
  rubric = null,
  excludeTeacherComment = true,
  showToast,
}) => {
  const validResults = (Array.isArray(results) ? results : []).filter(result => !result?.error);
  if (validResults.length === 0) throw new Error('다운로드할 채점 결과가 없어요.');

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;';
  document.body.appendChild(container);

  try {
    const elements = validResults.map((result) => {
      const element = document.createElement('div');
      element.style.cssText = 'width:700px;background:#fff;padding:32px;font-family:Pretendard,-apple-system,BlinkMacSystemFont,system-ui,sans-serif;margin-bottom:20px;';
      element.innerHTML = buildScoringResultPdfHtml(result, {
        excludeTeacherComment,
        rubric,
      });
      container.appendChild(element);
      return element;
    });
    await new Promise(resolve => setTimeout(resolve, 100));
    const names = validResults.map(result => getScoringStudentName(result, '학생'));
    const pdfBlob = await generateBulkSinglePdf(elements, names, { returnBlob: true });
    const pdfType = excludeTeacherComment ? '학생용' : '교사용';
    const filename = `채점결과_${pdfType}_${subject || '전체'}_${validResults.length}명.pdf`;
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    showToast?.('다운로드 폴더에 저장되었어요', 'success', 10000, {
      label: '바로 인쇄',
      onClick: () => printPdfBlob(pdfBlob),
    });
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return { filename, count: validResults.length };
  } finally {
    if (container.parentNode) document.body.removeChild(container);
  }
};
